import { useEffect, useRef, useState } from "react";
import {
  DOCTOR_QUESTION_STATUS_LABELS,
  type DoctorQuestionSummary,
  type MonthlyDiscussion,
  type MonthlyFollowUp,
} from "@care-by-ella/contracts";
import { useAuth } from "@/auth/AuthContext";
import {
  fetchDoctorQuestions,
  markDoctorQuestionHandled,
  UnauthorizedError,
} from "@/lib/clinician-api";
import { DoctorQuestionDetail, WithdrawQuestion } from "./DoctorQuestions";
import { SendDoctorNotice } from "./SendDoctorNotice";
import { useReadOnly } from "./SupportView";

// TES-1637 (Emelie 2026-10-28, F-05 in Drive) — the monthly case keeps her
// wish and its dialogue together:
//
//   "Visa patientens val och eventuell Annat-text tydligt tillsammans med
//    övrigt medicinskt månadsunderlag. Fritext skapar ingen separat
//    läkaruppgift och stoppar inte automatiskt receptförnyelse."
//   "Mer information krävs inför beslut: läkaren ställer en avgränsad
//    följdfråga som patienten kan besvara i Meddelanden → Läkare; samma
//    ärende får status Inväntar patientsvar och återkommer i läkarens kö
//    när svaret skickats in."
//
// Two parts, both inside the renewal panel:
//   - MonthlyDiscussionCard: "Vad vill du diskutera med läkaren?", the text
//     under "Annat", or on an older F-05 "Vilken fråga vill du helst ha svar
//     på?", in the words of the version she answered;
//   - MonthlyFollowUpQuestions: the case's follow-up questions and "Ställ en
//     följdfråga", a "Besked med fråga" tied to this case. The prescription
//     decision below stays available while one is open.
//
// "Inväntar patientsvar" and "Hanterat – inget nytt besked behövs" are the
// decisions' words; the rest is proposed copy.
//
// TES-1638 (Emelie and Kirill 2026-10-29) — from F-05 v16 the card shows two
// questions: the dose wish, then "Vill du ta upp något annat om din
// medicinska behandling?" with the text under "Annan medicinsk fråga". The
// card renders whatever items the server sends, in the version's own
// wording, so v15 and older answers read as before. A wish to pause or stop
// (value pausa_avsluta, asked from v2 on) is marked so it cannot be missed;
// nothing acts on it, the doctor decides.

const STOP_WISH = "pausa_avsluta";
const isStopWish = (i: MonthlyDiscussion["items"][number]) =>
  i.key === "month_treatment_preference" && i.value === STOP_WISH;

export function MonthlyDiscussionCard({
  cycleId,
  discussion,
}: {
  cycleId: string;
  discussion: MonthlyDiscussion | null | undefined;
}) {
  if (!discussion || discussion.items.length === 0) return null;
  return (
    <section
      className="rounded-md border border-brand/40 bg-brand/5 p-sm text-sm"
      data-testid={`renewal-${cycleId}-discussion`}
      aria-label="Patientens önskemål inför läkarens bedömning"
    >
      <p className="text-xs uppercase tracking-wide text-textSecondary">
        Patientens önskemål · månadsincheckning{" "}
        {discussion.submittedAt.slice(0, 10)}
      </p>
      <dl className="mt-xs flex flex-col gap-xs">
        {discussion.items.map((i) =>
          isStopWish(i) ? (
            <div
              key={i.key}
              className="rounded-sm border border-danger/50 bg-danger/5 p-xs"
              data-testid={`renewal-${cycleId}-discussion-stop-wish`}
            >
              <dt className="text-textSecondary">{i.label}</dt>
              <dd className="font-semibold whitespace-pre-wrap">{i.answer}</dd>
              <dd className="text-xs text-textSecondary">
                Inget ändras automatiskt. Ta ställning till önskemålet i ditt
                beslut.
              </dd>
            </div>
          ) : (
            <div key={i.key}>
              <dt className="text-textSecondary">{i.label}</dt>
              <dd className="font-semibold whitespace-pre-wrap">{i.answer}</dd>
            </div>
          ),
        )}
      </dl>
    </section>
  );
}

export function MonthlyFollowUpQuestions({
  patientId,
  cycleId,
  followUp,
  onChanged,
  onUnauthorized,
}: {
  patientId: string;
  cycleId: string;
  followUp: MonthlyFollowUp | null | undefined;
  /** Called after a question is asked, handled or withdrawn. */
  onChanged?: () => void;
  onUnauthorized: () => void;
}) {
  const { claims } = useAuth();
  const readOnly = useReadOnly();
  const canAct = claims?.role === "doctor" && !readOnly;
  const [questions, setQuestions] = useState<DoctorQuestionSummary[] | null>(
    null,
  );
  const [reload, setReload] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const unauthorized = useRef(onUnauthorized);
  unauthorized.current = onUnauthorized;

  useEffect(() => {
    let live = true;
    fetchDoctorQuestions(patientId)
      .then((res) => {
        if (!live) return;
        setQuestions(res.questions.filter((q) => q.cycleId === cycleId));
        setError(null);
      })
      .catch((e: unknown) => {
        if (!live) return;
        if (e instanceof UnauthorizedError) {
          unauthorized.current();
          return;
        }
        setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      live = false;
    };
  }, [patientId, cycleId, reload]);

  const changed = () => {
    setReload((n) => n + 1);
    onChanged?.();
  };
  // The server's status until the list is loaded, then the list's own, so a
  // question asked or handled here shows at once.
  const status: MonthlyFollowUp["status"] | null = questions
    ? questions.some((q) => q.status === "answered")
      ? "answered"
      : questions.some((q) => q.status === "waiting")
        ? "waiting"
        : null
    : (followUp?.status ?? null);

  return (
    <section
      className="flex flex-col gap-sm"
      data-testid={`renewal-${cycleId}-follow-up`}
    >
      <div className="flex flex-wrap items-center gap-sm">
        <h4 className="text-sm font-semibold">Följdfråga till patienten</h4>
        {status ? (
          <span
            className="rounded-sm bg-surfaceMuted px-xs text-xs text-textSecondary"
            data-testid={`renewal-${cycleId}-follow-up-status`}
          >
            {status === "waiting"
              ? DOCTOR_QUESTION_STATUS_LABELS.waiting
              : "Patienten har svarat"}
          </span>
        ) : null}
      </div>
      <p className="text-xs text-textSecondary">
        Behövs mer information kan du ställa en följdfråga. Patienten svarar i
        Meddelanden → Läkare och ärendet kommer tillbaka i din kö när hon har
        svarat. Du kan fatta receptbeslutet medan frågan är öppen.
      </p>
      {error ? (
        <p className="text-xs text-danger" role="alert">
          Följdfrågorna kunde inte hämtas: {error}
        </p>
      ) : null}
      {questions && questions.length > 0 ? (
        <ul className="flex flex-col gap-sm">
          {questions.map((q) => (
            <li
              key={q.taskId}
              className="rounded-md border border-surfaceMuted p-sm text-sm"
            >
              <DoctorQuestionDetail question={q} />
              {canAct && q.status === "answered" ? (
                <MarkHandled
                  patientId={patientId}
                  taskId={q.taskId}
                  onHandled={changed}
                  onUnauthorized={() => unauthorized.current()}
                />
              ) : null}
              <WithdrawQuestion
                patientId={patientId}
                question={q}
                onWithdrawn={changed}
                onUnauthorized={() => unauthorized.current()}
              />
            </li>
          ))}
        </ul>
      ) : null}
      <SendDoctorNotice
        patientId={patientId}
        onUnauthorized={onUnauthorized}
        monthlyCase={{ cycleId }}
        onSent={changed}
      />
    </section>
  );
}

function MarkHandled({
  patientId,
  taskId,
  onHandled,
  onUnauthorized,
}: {
  patientId: string;
  taskId: string;
  onHandled: () => void;
  onUnauthorized: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <div className="mt-sm flex flex-col gap-xs">
      <button
        type="button"
        disabled={busy}
        className="self-start rounded-md border border-surfaceMuted px-sm py-xs text-xs text-textSecondary hover:text-textPrimary disabled:opacity-50"
        onClick={() => {
          setBusy(true);
          setError(null);
          markDoctorQuestionHandled(patientId, taskId)
            .then(onHandled)
            .catch((e: unknown) => {
              if (e instanceof UnauthorizedError) onUnauthorized();
              else setError("Svaret kunde inte markeras som hanterat.");
            })
            .finally(() => setBusy(false));
        }}
      >
        Hanterat – inget nytt besked behövs
      </button>
      {error ? (
        <p className="text-xs text-danger" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
